import { createElement, Fragment, ReactNode } from 'react';

import { Language, Languages } from '../../model/translations/Language';
import { ddlog } from '../miscellaneous';

/**
 * Detects the browser's preferred language and returns a supported Language.
 *
 * Uses the browser's language preferences to find the best matching supported language.
 * If no supported language is found, returns 'en' as default.
 */
export function detectBrowserLanguage(defaultLanguage: Language = 'en'): Language {
  try {
    const browserLanguages = navigator.languages || [navigator.language];

    // Check each browser language preference
    for (const browserLang of browserLanguages) {
      if (browserLang) {
        // Normalize the browser language (e.g., 'pt-BR' -> 'pt-br')
        const normalizedLang = browserLang.toLowerCase();

        // First check for exact match (e.g., 'pt-br')
        if (Languages.includes(normalizedLang as Language)) {
          return normalizedLang as Language;
        }

        // Then check primary language code (e.g., 'en' from 'en-us')
        const primaryLang = normalizedLang.split('-')[0];
        if (Languages.includes(primaryLang as Language)) {
          return primaryLang as Language;
        }
      }
    }
  } catch (error) {
    ddlog.warn('Error detecting browser language:', { error });
  }

  return defaultLanguage;
}

/**
 * Replaces placeholders in a translation string with actual values.
 *
 * Placeholders should be in the format !{key}, where key matches a property in the values object.
 * Example: formatTranslation("Enter this code to join !{name}", {name: "Class A"})
 * Returns: "Enter this code to join Class A"
 *
 * @param template - The translation string containing placeholders
 * @param values - An object with key-value pairs to replace in the template
 * @returns The formatted string with placeholders replaced
 */
export function formatTranslation(template?: string, values?: Record<string, string | number | undefined>): string {
  if (!template) {
    return template || '';
  }

  return template.replace(/!\{(\w+)\}/g, (match, key) => {
    const value = values?.[key];
    return value !== undefined ? String(value) : match;
  });
}

/**
 * Like formatTranslation, but each value may be a React node (for example a bold lesson
 * title). Values are placed as nodes, never as HTML, so text in them is always shown
 * literally. Placeholders with no value are left as written.
 */
export function formatTranslationNodes(template: string, values: Record<string, ReactNode>): ReactNode[] {
  const nodes: ReactNode[] = [];
  let lastIndex = 0;

  for (const match of template.matchAll(/!\{(\w+)\}/g)) {
    const [token, key] = match;
    const index = match.index ?? 0;
    if (!Object.prototype.hasOwnProperty.call(values, key) || values[key] === undefined) {
      continue;
    }
    if (index > lastIndex) {
      nodes.push(template.slice(lastIndex, index));
    }
    nodes.push(createElement(Fragment, { key: `${key}-${index}` }, values[key]));
    lastIndex = index + token.length;
  }

  if (lastIndex < template.length) {
    nodes.push(template.slice(lastIndex));
  }
  return nodes;
}
